import { COLS, ROWS } from './board.js';
import { MATCH_GLOW } from './scale.js';
import { ParticleSystem } from './particles.js';
import {
  drawCellWell,
  drawCloudGlassFrame,
  drawPuffyNoteBlock,
  setupHiDpiCanvas,
} from './illustrativeDraw.js';

const GAP = 2;
const FRAME = 12;
const GRID_ASPECT = COLS / ROWS;

function gridSizeForCell(cell) {
  const gridW = COLS * (cell + GAP) + GAP;
  const gridH = ROWS * (cell + GAP) + GAP;
  return {
    cell,
    cssW: gridW + FRAME * 2,
    cssH: gridH + FRAME * 2,
    gridW,
    gridH,
  };
}

function pickCellSize(maxW, maxH) {
  const cellFromH = Math.floor((maxH - FRAME * 2 - GAP) / ROWS - GAP);
  const cellFromW = Math.floor((maxW - FRAME * 2 - GAP) / COLS - GAP);
  let cell = Math.max(14, Math.min(56, Math.min(cellFromH, cellFromW)));
  let layout = gridSizeForCell(cell);
  while (layout.cssH > maxH && cell > 14) {
    cell -= 1;
    layout = gridSizeForCell(cell);
  }
  return layout;
}

export class Renderer {
  constructor(boardCanvas, nextCanvas, boardFrame, panelEl, mountEl) {
    this.boardCanvas = boardCanvas;
    this.nextCanvas = nextCanvas;
    this.boardFrame = boardFrame;
    this.panelEl = panelEl;
    this.mountEl = mountEl;
    this.gap = GAP;
    this.frame = FRAME;
    this.particles = new ParticleSystem();
    this.glowCells = [];
    this.lastTs = 0;

    this.applyLayout();
  }

  boardBudget() {
    const slot = this.boardFrame?.closest('.mct-board-slot');
    if (slot && slot.clientHeight > 48) {
      return Math.max(100, slot.clientHeight - 4);
    }

    const panel = this.panelEl;
    const body = panel?.querySelector('.mct-body');
    if (body && body.clientHeight > 48) {
      return Math.max(100, body.clientHeight - 16);
    }

    const topH = panel?.querySelector('.mct-top-hud')?.offsetHeight ?? 40;
    const footerH = panel?.querySelector('.mct-harmony-bar')?.offsetHeight ?? 44;
    const mountH = this.mountEl?.clientHeight ?? window.innerHeight;
    const panelPad = 18;
    const statusH = 14;
    return Math.max(100, mountH - topH - footerH - panelPad - statusH);
  }

  applyLayout() {
    const budgetH = this.boardBudget();
    const maxH = budgetH;
    const maxW = Math.max(140, maxH * GRID_ASPECT + FRAME * 2 + 12);

    const layout = pickCellSize(maxW, maxH);

    this.cellSize = layout.cell;
    this.cssW = layout.cssW;
    this.cssH = layout.cssH;
    this.gridOffsetX = FRAME;
    this.gridOffsetY = FRAME;
    this.innerW = layout.gridW + 12;
    this.innerH = layout.gridH + 12;

    const boardSetup = setupHiDpiCanvas(this.boardCanvas, this.cssW, this.cssH);
    this.boardCtx = boardSetup.ctx;

    if (this.boardFrame) {
      this.boardFrame.style.width = `${this.cssW}px`;
      this.boardFrame.style.height = `${this.cssH}px`;
    }

    const nextCell = Math.round(this.cellSize * 0.74);
    const nextGap = 2;
    this.nextCssW = 5 * (nextCell + nextGap) + nextGap + 20;
    this.nextCssH = 5 * (nextCell + nextGap) + nextGap + 20;
    this.nextCell = nextCell;
    this.nextGap = nextGap;
    const nextSetup = setupHiDpiCanvas(this.nextCanvas, this.nextCssW, this.nextCssH);
    this.nextCtx = nextSetup.ctx;
  }

  triggerMatchFx(matches) {
    const now = performance.now();
    for (const m of matches) {
      const glow = MATCH_GLOW[m.rule] ?? { color: '#fff', label: 'Match' };
      for (const c of m.cells) {
        this.glowCells.push({ x: c.x, y: c.y, rule: m.rule, start: now, duration: 1100 });
      }
      const points = m.cells.map((c) => ({
        x: this.gridOffsetX + this.gap + c.x * (this.cellSize + this.gap) + this.cellSize / 2,
        y: this.gridOffsetY + this.gap + c.y * (this.cellSize + this.gap) + this.cellSize / 2,
        color: glow.color,
      }));
      this.particles.burstMany(points, glow.color);
    }
  }

  cellPx(x, y) {
    return {
      x: this.gridOffsetX + this.gap + x * (this.cellSize + this.gap),
      y: this.gridOffsetY + this.gap + y * (this.cellSize + this.gap),
    };
  }

  getGlowAt(x, y, now) {
    for (const g of this.glowCells) {
      if (g.x === x && g.y === y) {
        const t = (now - g.start) / g.duration;
        if (t <= 1) {
          const info = MATCH_GLOW[g.rule];
          return { color: info?.color ?? '#fff', pulse: 1 - t };
        }
      }
    }
    return null;
  }

  pruneGlow(now) {
    this.glowCells = this.glowCells.filter((g) => now - g.start < g.duration);
  }

  ghostBlocks(piece, board) {
    if (!piece) return [];
    const gy = piece.y;
    let dropY = gy;
    piece.move(0, 1);
    while (!board.collides(piece)) {
      dropY = piece.y;
      piece.move(0, 1);
    }
    const delta = dropY - gy;
    piece.y = gy;
    if (delta <= 0) return [];
    return piece.blocks().map((b) => ({ ...b, y: b.y + delta }));
  }

  drawPlayfieldGrid(ctx, cols, rows) {
    for (let y = 0; y < rows; y++) {
      for (let x = 0; x < cols; x++) {
        const { x: px, y: py } = this.cellPx(x, y);
        drawCellWell(ctx, px, py, this.cellSize);
      }
    }
  }

  renderBoard(board, activePiece, ts = 0) {
    const ctx = this.boardCtx;
    const now = ts || performance.now();
    if (this.lastTs) this.particles.update(ts - this.lastTs || 16);
    this.lastTs = ts || now;
    this.pruneGlow(now);

    ctx.clearRect(0, 0, this.cssW, this.cssH);
    const innerX = this.gridOffsetX - 6;
    const innerY = this.gridOffsetY - 6;
    drawCloudGlassFrame(ctx, this.cssW, this.cssH, innerX, innerY, this.innerW, this.innerH);
    this.drawPlayfieldGrid(ctx, board.cols, board.rows);

    const ghost = this.ghostBlocks(activePiece, board);
    for (const block of ghost) {
      if (block.y < 0) continue;
      const { x: px, y: py } = this.cellPx(block.x, block.y);
      drawPuffyNoteBlock(ctx, px, py, this.cellSize, block.note, block.color, { ghost: true });
    }

    for (let y = 0; y < board.rows; y++) {
      for (let x = 0; x < board.cols; x++) {
        const cell = board.grid[y][x];
        if (!cell) continue;
        const { x: px, y: py } = this.cellPx(x, y);
        const glowFx = this.getGlowAt(x, y, now);
        drawPuffyNoteBlock(ctx, px, py, this.cellSize, cell.note, cell.color, {
          glow: glowFx?.color ?? null,
          glowPulse: glowFx?.pulse ?? 0,
        });
      }
    }

    if (activePiece) {
      for (const block of activePiece.blocks()) {
        if (block.y < 0) continue;
        const { x: px, y: py } = this.cellPx(block.x, block.y);
        drawPuffyNoteBlock(ctx, px, py, this.cellSize, block.note, block.color, { alpha: 0.98 });
      }
    }

    this.particles.draw(ctx, now);
  }

  renderNext(piece) {
    const ctx = this.nextCtx;
    const sz = this.nextCell;
    const gap = this.nextGap;
    ctx.clearRect(0, 0, this.nextCssW, this.nextCssH);

    ctx.fillStyle = 'rgba(241, 245, 249, 0.95)';
    ctx.beginPath();
    ctx.roundRect(0, 0, this.nextCssW, this.nextCssH, 12);
    ctx.fill();
    ctx.strokeStyle = 'rgba(203, 213, 225, 0.8)';
    ctx.lineWidth = 1.5;
    ctx.stroke();

    if (!piece) return;

    const matrix = piece.matrix;
    const noteGrid = piece.noteGrid;
    const rows = matrix.length;
    const cols = matrix[0].length;
    const offsetX = Math.floor((5 - cols) / 2);
    const offsetY = Math.floor((5 - rows) / 2);
    const ox = 10;
    const oy = 10;

    for (let y = 0; y < rows; y++) {
      for (let x = 0; x < cols; x++) {
        if (!matrix[y][x] || !noteGrid[y][x]) continue;
        const px = ox + gap + (offsetX + x) * (sz + gap);
        const py = oy + gap + (offsetY + y) * (sz + gap);
        drawPuffyNoteBlock(ctx, px, py, sz, noteGrid[y][x].note, noteGrid[y][x].color);
      }
    }
  }
}
